import { type ReactNode } from 'react';
import { Sidebar } from '../components/sidebar';
import Footer from '../components/Footer';
import DemoBanner from '../components/DemoBanner';
import LanguageSelector from '../components/LanguageSelector';
import ScrollArea from '../components/ScrollArea';
import ErrorBoundary from '../components/ErrorBoundary';
import { useLayoutData } from '../hooks/useLayoutContext';

type DesktopLayoutProps = {
  children: ReactNode;
};

/**
 * DesktopLayout — Desktop page shell for Ronan v2.
 *
 * Reads sidebar/layout data from LayoutContext.
 * Structure: persistent sidebar (left) + scrollable main content (right).
 */
export default function DesktopLayout({ children }: DesktopLayoutProps) {
  const {
    client,
    requestType,
    interactionConfig,
    requestedDataGroups,
    requestedDataLoading,
    loading,
    demo,
  } = useLayoutData();

  return (
    <div className="flex flex-col h-dvh w-full font-inter">
      {demo && <DemoBanner />}
      <div className="flex flex-1 min-h-0 w-full">
        <aside className="flex flex-col bg-sidebar w-lg shrink-0">
          <ScrollArea className="pl-10 pr-12">
            <Sidebar
              client={client}
              requestType={requestType}
              interactionConfig={interactionConfig}
              requestedDataGroups={requestedDataGroups}
              requestedDataLoading={requestedDataLoading}
              loading={loading}
            />
          </ScrollArea>
        </aside>

        <div className="flex flex-1 flex-col min-w-0 bg-background relative pt-5">
          <div className="absolute right-5 top-5">
            <LanguageSelector />
          </div>

          <main className="flex flex-1 flex-col items-center overflow-y-auto px-10 pb-10">
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
}
